import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Calendar } from '@vendasta/meetings';
import { FeatureFlagFieldType } from '@vendasta/meetings';
import {
  combineLatest,
  EMPTY,
  Observable,
  of,
  ReplaySubject,
  Subject,
  Subscription,
} from 'rxjs';
import {
  catchError,
  distinctUntilChanged,
  filter,
  map,
  shareReplay,
  switchMap,
  take,
  tap,
} from 'rxjs/operators';
import { BookMeNowStoreService } from '../book-me-now-store.service';
import { MeetingInfo } from '../bookme-calendar/meeting-info/meeting-info';
import { TimeSlot, TimezoneRegionAndZone } from '../bookme/bookme';
import {
  getTimezone,
  getZoneFromTimezone,
  trimTimeRangeToFromNow,
} from '../util';

@Component({
  selector: 'app-bookme-reschedule',
  templateUrl: './bookme-reschedule.component.html',
  styleUrls: ['./bookme-reschedule.component.scss'],
  standalone: false,
})
export class BookmeRescheduleComponent implements OnInit, OnDestroy {
  calendar$: Observable<Calendar>;
  meetingId: string;
  loadTimeSlotsError: Error;

  private readonly rescheduleIds$$ = new ReplaySubject<{ meetingId: string }>(
    1
  );
  private loadTimeSlots$$: Subject<{
    timeSpan?: TimeSlot;
    timezone?: string;
  }> = new Subject();
  private readonly subscriptions: Subscription[] = [];
  rescheduledMeetingInfo$: Observable<MeetingInfo>;

  timeSlots$: Observable<TimeSlot[]>;
  timezone$: Observable<string>;
  timezoneOffset$: Observable<string>;
  displayName: string;
  calendarId: string;
  isSmsFeatureFlagEnabled = false;

  constructor(
    private readonly bookmenowService: BookMeNowStoreService,
    private readonly route: ActivatedRoute,
    private readonly router: Router
  ) {}

  ngOnInit(): void {
    this.rescheduledMeetingInfo$ = this.rescheduleIds$$.pipe(
      filter((v) => !!v && !!v.meetingId), // Require all values
      switchMap((ids) =>
        combineLatest([
          of(ids),
          this.bookmenowService.getBookedMeetingInfo(ids.meetingId),
        ])
      ),
      map(([ids, meeting]) => {
        this.bookmenowService.setMeetingToRescheduleOrCancel(ids.meetingId);
        return meeting;
      }),
      catchError((err: HttpErrorResponse) => {
        console.error('get-booked-meeting-info', err);
        this.bookmenowService.handleMeetingError(err, 'reschedule');
        return EMPTY;
      }),
      map((info) => {
        this.calendarId = info.calendarId ? info.calendarId : '';
        this.displayName = info.displayName;

        // Store the original meeting time slot for comparison in confirmation pages
        const originalTimeSlot: TimeSlot = {
          start: info.start,
          end: info.end,
          timezone: this.createTimezone(
            info.start,
            info.timeZone ? info.timeZone.id : getTimezone()
          ),
        };
        this.bookmenowService.setOriginalMeetingTimeSlot(originalTimeSlot);

        return {
          startTime: info.start,
          endTime: info.end,
          timezone: this.createTimezone(
            info.start,
            info.timeZone ? info.timeZone.id : getTimezone()
          ),
          meetingTypeId: info.meetingTypeId,
          location: info.location,
          locationType: info.locationType,
          locationGuideline: info.locationGuideline,
          displayName: info.displayName,
          calendarId: info.calendarId,
          meetingType: info.meetingType,
        };
      }),
      shareReplay(1)
    );

    // Check feature flag for SMS notification when calendar is loaded
    this.subscriptions.push(
      this.bookmenowService.calendar$
        .pipe(filter<Calendar>(Boolean), take(1))
        .subscribe((calendar) => {
          this.checkSmsFeatureFlag(calendar.id);
        })
    );

    // Pre-fetch guest booked meeting V2 data for confirmation pages
    const meetingId = this.route.snapshot.params.meetingId;
    this.subscriptions.push(
      this.bookmenowService
        .getGuestBookedMeetingV2({ id: meetingId })
        .pipe(
          catchError((err) => {
            console.error('Failed to fetch guest booked meeting V2:', err);
            this.bookmenowService.handleMeetingError(err, 'reschedule');
            return EMPTY;
          })
        )
        .subscribe((meetingInfo) => {
          this.bookmenowService.setGuestBookedMeetingV2(meetingInfo);
        })
    );

    this.subscriptions.push(
      this.rescheduledMeetingInfo$
        .pipe(
          map((info) => info.timezone.region),
          distinctUntilChanged(),
          filter<string>(Boolean)
        )
        .subscribe((timezone) => {
          if (!this.bookmenowService.getTimezoneChanged()) {
            this.onTimeZoneChanged(timezone, true);
          }
        })
    );

    const meetingTypeId$ = this.rescheduledMeetingInfo$.pipe(
      filter<MeetingInfo>(Boolean),
      map((meeting) => meeting.meetingTypeId),
      distinctUntilChanged()
    );

    this.timeSlots$ = combineLatest([
      this.loadTimeSlots$$,
      meetingTypeId$,
    ]).pipe(
      switchMap(([req, meetingTypeId]) => {
        return this.bookmenowService.loadAvailableTimeSlots({
          timeSpan: trimTimeRangeToFromNow(req.timeSpan),
          timeZone: req.timezone ? { id: req.timezone } : undefined,
          calendarId: this.calendarId,
          meetingTypeId,
        });
      }),
      tap(() => (this.loadTimeSlotsError = undefined)),
      catchError((err) => {
        console.error(err);
        this.loadTimeSlotsError = new Error(
          $localize`:@@BOOK.ERROR_OCCURRED:An error occurred.`
        );
        return of([]);
      })
    );

    this.timezone$ = this.bookmenowService.timezone$;
    this.timezoneOffset$ = this.bookmenowService.timezoneOffset$;

    this.rescheduleIds$$.next({
      meetingId: this.route.snapshot.params.meetingId,
    });
    this.calendar$ = this.bookmenowService.calendar$;
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((s) => s.unsubscribe());
  }

  onTimeSlotSelected(timeSlot: TimeSlot): void {
    // Calculate the correct end time based on the original meeting duration
    this.rescheduledMeetingInfo$
      .pipe(
        filter<MeetingInfo>(Boolean),
        map((meetingInfo) => {
          // Calculate the original meeting duration in milliseconds
          const originalDuration =
            new Date(meetingInfo.endTime).getTime() -
            new Date(meetingInfo.startTime).getTime();

          // Apply the original duration to the newly selected start time
          const correctedEndTime = new Date(
            timeSlot.start.getTime() + originalDuration
          );

          return {
            ...timeSlot,
            end: correctedEndTime,
          };
        }),
        tap((correctedTimeSlot) => {
          this.bookmenowService.setSelectedTimeSlot(correctedTimeSlot);
        })
      )
      .subscribe();
  }

  onLocationChange(location: string): void {
    this.bookmenowService.setLocation(location);
  }

  onConfirmChanges(): void {
    const route = this.isSmsFeatureFlagEnabled
      ? 'confirm-changes'
      : 'reschedule-confirmation';
    this.router.navigate([route], {
      relativeTo: this.route,
      queryParamsHandling: 'preserve',
    });
  }

  onTimeRangeChanged(timeRange: { start: Date; end: Date }): void {
    this.bookmenowService.setSelectedTimeRange(timeRange);
    this.requestAvailability({ timeRange });
  }

  onTimeZoneChanged(timezone: string, isInitialSet: boolean): void {
    this.bookmenowService.setTimezone(timezone, isInitialSet);
    this.requestAvailability({ timezone });
  }

  createTimezone(time: Date, timezone: string): TimezoneRegionAndZone {
    const timezoneRegion = timezone;
    const timezoneOffset = time.getTimezoneOffset();

    return {
      region: timezoneRegion,
      zone: getZoneFromTimezone(timezoneRegion, timezoneOffset),
    };
  }

  private requestAvailability(req: {
    timeRange?: TimeSlot;
    timezone?: string;
  }): void {
    this.loadTimeSlots$$.next({
      timeSpan: req.timeRange,
      timezone: req.timezone,
    });
  }

  private checkSmsFeatureFlag(calendarId: string): void {
    this.subscriptions.push(
      this.bookmenowService
        .checkFeatureFlag({
          fieldType: FeatureFlagFieldType.FEATURE_FLAG_FIELD_TYPE_CALENDAR_ID,
          fieldValue: calendarId,
          featureFlagKey: 'meeting_scheduler_sms_notification',
        })
        .subscribe((isEnabled) => {
          this.isSmsFeatureFlagEnabled = isEnabled;
        })
    );
  }
}
